import { isString } from 'lodash';
import { KeyboardEvent, MouseEvent, ReactNode, useCallback, useRef } from 'react';
import { RowContent, RowContentProps } from '@shared/components/RowContent';
import { ButtonView, ButtonViewProps, View, ViewProps } from '@shared/components/View';
import {
  useIsMenuTrigger,
  useIsSubmenuTrigger,
  useMenuContext,
  useRootMenuContext,
} from './MenuContext';
import styles from './MenuItem.module.css';

/**
 * Menu.Item / Menu.Row
 *
 * `Menu.Item` is a clickable row inside a menu. It closes the menu on click
 * (respecting `closeOnSelect`) and supports icons, start/end content, and a
 * `danger` style for destructive actions.
 *
 * `Menu.Row` is the non-interactive counterpart — same layout, but rendered as
 * a `View` instead of a button. Use it for read-only rows or custom content
 * that manages its own interactivity.
 */

interface MenuRowProps extends RowContentProps, ViewProps {
  children?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
}

export function MenuRow({
  children,
  label,
  numberOfLines,
  icon,
  iconSize,
  startContent,
  endContent,
  danger,
  disabled,
  className,
  onClick,
  ...rest
}: MenuRowProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Emulate button behavior for keyboard navigation. Requires that the row has
  // an onClick prop and a focusable element inside it
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        onClick?.(event as unknown as MouseEvent<HTMLDivElement>);
      }
    },
    [onClick],
  );

  return (
    <View
      ref={ref}
      className={[styles.menuRow, danger && styles.danger, disabled && styles.disabled, className]}
      role="menuitem"
      aria-label={isString(label) ? label : undefined}
      onClick={onClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      tabIndex={-1}
      {...rest}
    >
      <RowContent
        label={label}
        icon={icon}
        iconSize={iconSize}
        startContent={startContent}
        endContent={endContent}
        numberOfLines={numberOfLines}
      >
        {children}
      </RowContent>
    </View>
  );
}

interface MenuItemProps extends RowContentProps, Omit<ButtonViewProps, 'label'> {
  children?: ReactNode;
  closeOnSelect?: boolean;
  danger?: boolean;
}

export function MenuItem({
  children,
  label,
  numberOfLines,
  onClick,
  disabled = false,
  closeOnSelect,
  icon,
  iconSize,
  startContent,
  endContent,
  danger,
  className,
  ...rest
}: MenuItemProps) {
  const menu = useMenuContext();
  const rootMenu = useRootMenuContext();
  const isMenuTrigger = useIsMenuTrigger();
  const isSubmenuTrigger = useIsSubmenuTrigger();
  const isTrigger = isMenuTrigger || isSubmenuTrigger;
  const isMenuOpen = isMenuTrigger && menu.isOpen;
  const isSubmenuOpen = isSubmenuTrigger && menu.isOpen;
  const isOpen = isMenuOpen || isSubmenuOpen;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (disabled) {
      return;
    }
    onClick?.(event);
    if (isTrigger) {
      return;
    }
    if (rootMenu?.closeOnSelect) {
      rootMenu.close();
      return;
    }
    if (closeOnSelect ?? menu.closeOnSelect) {
      menu.close();
    }
  };

  return (
    <ButtonView
      className={[
        styles.menuRow,
        styles.menuItem,
        danger && styles.danger,
        disabled && styles.disabled,
        isMenuTrigger && styles.menuTrigger,
        isSubmenuTrigger && styles.submenuTrigger,
        className,
      ]}
      onClick={handleClick}
      disabled={disabled}
      role="menuitem"
      aria-label={isString(label) ? label : undefined}
      aria-haspopup={isTrigger ? 'menu' : undefined}
      aria-expanded={isTrigger ? isOpen : undefined}
      {...rest}
    >
      <RowContent
        icon={icon}
        iconSize={iconSize}
        startContent={startContent}
        label={label}
        numberOfLines={numberOfLines}
        endContent={endContent}
      >
        {children}
      </RowContent>
    </ButtonView>
  );
}
